import React, { useCallback, useMemo, useState } from "react";
import { useI18n } from "../../../../hooks/i18n-hooks";
import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";
import { BodyMediumText, LinkSmallTextBold, TitleMediumText } from "../../../FormattedText";
import { StackScreenProps } from "@react-navigation/stack";
import { IJMEditorStackParamList } from "../types";
import { useIJMEditorContext } from "../context";
import { BubbleTalk } from "../../../BubbleTalk";
import DocuIllSvg from "../../../../assets/images/docu_ill.svg";
import { useQuery } from "@apollo/client/react";
import { GET_IJM_GROUPS } from "../../../../graphql/client";
import {
    getPayRemainingPercentsSetAndLabel,
    getPayFirstDaysSetAndLabel,
    isSelectionValid,
} from "../helper";
import ActionButton from "../../../ActionButton";
import { useTheme } from "../../../../hooks/use-theme";
import Gap from "../../../../ui_atoms/Gap";
import Separator from "../../../../ui_atoms/separator";
import AutoSizeImage from "../../../AutoSizeImage";
import { useIntercom } from "../../../../hooks/intercom";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import OptionsField from "../../../OptionsField";

type IJMRootScreenProps = StackScreenProps<IJMEditorStackParamList, "IJM">;

const IJMRootScreen: React.FC<IJMRootScreenProps> = ({ navigation }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { selection, setSelection, cct, useLse, onSave } = useIJMEditorContext();
    const isValid = useMemo(
        () => isSelectionValid(selection, cct, useLse),
        [selection, cct, useLse],
    );
    const [isLoading, setIsLoading] = useState(false);
    const { presentArticle } = useIntercom();
    const handleSave = useCallback(async () => {
        setIsLoading(true);
        try {
            await onSave?.(selection);
        } finally {
            setIsLoading(false);
        }
    }, [onSave, selection]);

    const handleCancel = useCallback(async () => {
        await onSave?.(null);
    }, [onSave]);

    const { data } = useQuery(GET_IJM_GROUPS, {
        fetchPolicy: "cache-and-network",
    });
    const currentGroup = useMemo(() => {
        return (data?.getIjmGroups || []).find((ijmChoice) => {
            return ijmChoice.id === selection.ijmGroupId;
        });
    }, [data?.getIjmGroups, selection.ijmGroupId]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("save"),
                onPress: handleSave,
                mode: "secondary",
                validating: isLoading,
                isValid: isValid && !isLoading,
            },
            {
                title: i18n.t("cancel"),
                onPress: handleCancel,
                mode: "soft",
                isValid: !isLoading,
            },
        ];
    }, [i18n, handleSave, isValid, isLoading, handleCancel]);

    const [ijmPayFirstDaysSet, ijmPayFirstDaysText] = useMemo(() => {
        return getPayFirstDaysSetAndLabel(selection.ijmPayFirstDays);
    }, [selection.ijmPayFirstDays]);

    const [ijmPayRemainingPercentSet, ijmPayRemainingPercentText] = useMemo(() => {
        return getPayRemainingPercentsSetAndLabel(selection.ijmPayRemainingPercents);
    }, [selection.ijmPayRemainingPercents]);

    const editable = true; // Always editable currently

    const insets = useSafeAreaInsets();
    const ijmTextKey = useMemo(() => {
        if (useLse) {
            return "ijm.editor.root.text_lse";
        }
        if (cct && cct.isIjmMandatory) {
            return "ijm.editor.root.text_is_mandatory";
        }
        return "ijm.editor.root.text_not_mandatory";
    }, [useLse, cct]);
    const shouldAskIjm = !useLse && !cct?.isIjmMandatory;
    return (
        <ScreenCard style={{ paddingTop: insets.top }} buttons={buttons}>
            <VStack>
                <VStack h="2/3">
                    <Gap gap={0.5} />
                    <BubbleTalk
                        personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                        personSize={44}
                        position="top-center"
                    >
                        <TitleMediumText>{i18n.t("ijm.editor.root.title")}</TitleMediumText>
                    </BubbleTalk>
                    <Gap gap={1} />
                    <BodyMediumText>{i18n.t(ijmTextKey, { cctName: cct?.name })}</BodyMediumText>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticle("IJMGroupClient");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </VStack>
                <Separator v={1 / 2} />
                {shouldAskIjm && (
                    <VStack h="2/3">
                        <TitleMediumText>{i18n.t("ijm.editor.root.wantIjm")}</TitleMediumText>
                        <OptionsField
                            value={
                                selection?.ijmGroupId === undefined
                                    ? selection?.ijmGroupId
                                    : !!selection?.ijmGroupId
                            }
                            data={[
                                { label: i18n.t("yes"), value: true },
                                { label: i18n.t("no"), value: false },
                            ]}
                            onSelect={(value) => {
                                if (value) {
                                    setSelection({
                                        ijmGroupId: "need",
                                    });
                                } else {
                                    setSelection({ ijmGroupId: null });
                                }
                            }}
                            shrinked
                        />
                        <Gap gap={1} />
                    </VStack>
                )}
                {(!shouldAskIjm || !!selection?.ijmGroupId) && (
                    <VStack h="2/3">
                        {/* Group select */}
                        <TitleMediumText>{i18n.t("ijm.editor.root.title_2")}</TitleMediumText>
                        <Gap gap={0.5} />

                        <BodyMediumText>{i18n.t("ijm.editor.group_select.title")}</BodyMediumText>
                        <Gap gap={1 / 4} />
                        <ActionButton
                            text={
                                currentGroup
                                    ? currentGroup.name
                                    : i18n.t("ijm.ijm_group.place_holder")
                            }
                            textColor={currentGroup ? colors.text : colors.greyAccessible}
                            textWidth={currentGroup ? "bold" : "medium"}
                            rightIcon={
                                editable
                                    ? {
                                          name: "chevron-down",
                                          color: colors.text,
                                      }
                                    : undefined
                            }
                            onPress={() => {
                                navigation.navigate("GroupSelect", { useLse });
                            }}
                        />
                        <Gap gap={1} />
                        {useLse && (
                            <>
                                {/* Waiting period select */}
                                <BodyMediumText>
                                    {i18n.t("ijm.editor.waiting_select.title")}
                                </BodyMediumText>
                                <Gap gap={1 / 4} />
                                <ActionButton
                                    text={i18n.t(ijmPayFirstDaysText)}
                                    textColor={
                                        ijmPayFirstDaysSet ? colors.text : colors.greyAccessible
                                    }
                                    textWidth={ijmPayFirstDaysSet ? "bold" : "medium"}
                                    rightIcon={
                                        editable
                                            ? {
                                                  name: "chevron-down",
                                                  color: colors.text,
                                              }
                                            : undefined
                                    }
                                    onPress={() => navigation.navigate("WaitingSelect")}
                                />
                                <Gap gap={1} />
                            </>
                        )}

                        {/* First days coverage */}
                        <BodyMediumText>{i18n.t("ijm.editor.cover_select.title")}</BodyMediumText>
                        <Gap gap={1 / 4} />
                        <ActionButton
                            text={i18n.t(ijmPayRemainingPercentText)}
                            textColor={
                                ijmPayRemainingPercentSet ? colors.text : colors.greyAccessible
                            }
                            textWidth={ijmPayRemainingPercentSet ? "bold" : "medium"}
                            numberOfLines={3}
                            rightIcon={
                                editable
                                    ? {
                                          name: "chevron-down",
                                          color: colors.text,
                                      }
                                    : undefined
                            }
                            onPress={() => navigation.navigate("CoverSelect")}
                        />
                        {!useLse && (
                            <>
                                <Gap gap={1} />
                                <BodyMediumText>
                                    {i18n.t("ijm.editor.root.no_lse_comment")}
                                </BodyMediumText>
                            </>
                        )}
                        <Gap gap={1.5} />
                        <VStack alignment="center" style={{ opacity: 0.5 }}>
                            <BodyMediumText>
                                {i18n.t("ijm.editor.root.collaboration")}
                            </BodyMediumText>
                            <Gap gap={1 / 2} />
                            <AutoSizeImage
                                source={require("../../../../assets/images/groupe_mutuel.png")}
                                fixedHeight={40}
                            />
                        </VStack>
                    </VStack>
                )}
            </VStack>
        </ScreenCard>
    );
};

export default IJMRootScreen;
